"use client";

import React, { useState } from "react";
import { Container, Section } from "@/components/craft";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { PhoneNumberInput } from "@/components/phonenumber-input";
import { DateInput } from "@/components/date-input";
import { CurrencyBudgetInput } from "@/components/currency-budget-input";

const formSchema = z.object({
  name: z
    .string()
    .min(3, "Name must be at least 3 characters")
    .refine((val) => /^[a-zA-Z\s]*$/.test(val), {
      message: "Name should contain only letters and spaces",
    }),

  email: z
    .string()
    .email("Please enter a valid email address")
    .min(5, "Email is too short"),

  phone: z.string().min(8, "Enter a valid phone number"),

  projectType: z.string().nonempty("Please select a project type"),

  budget: z.string().min(1, "Please provide a budget estimate"),

  deadline: z.string().optional(),

  message: z
    .string()
    .min(
      30,
      "Please provide a detailed description of your project (at least 30 characters)"
    )
    .max(2000, "Project description is too long (maximum 2000 characters)"),
});

type FormData = z.infer<typeof formSchema>;

const ContactPage = () => {
  const [phone, setPhone] = useState("");
  const [budget, setBudget] = useState("₹");
  const [deadline, setDeadline] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false); // Track form submission status
  const [isSuccess, setIsSuccess] = useState(false); // Track success or failure

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setIsSubmitted(false);

      const finalData = {
        ...data,
        phone,
        budget,
        deadline,
      };

      const response = await fetch("/api/client", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      if (!response.ok) {
        throw new Error("There was an issue with the form submission.");
      }

      const result = await response.json();

      setIsSuccess(true);
      toast.success(
        "✅ Message sent successfully! We'll get in touch shortly 🚀"
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsSuccess(false);
      toast.error(
        "There was a problem sending your message. Please try again."
      );
    } finally {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }
  };

  return (
    <Section>
      <Container className='mx-auto max-w-2xl space-y-6'>
        {!isSubmitted ? (
          <>
            <div className='space-y-2 text-center'>
              <h2 className='text-3xl font-semibold'>
                🎁 Claim Your FREE Month + Website
              </h2>
              <p className='text-muted-foreground'>
                Get 1 month of free social media management + a personalized
                website. Fill out the form and we'll reach out within 24 hours!
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className='space-y-5'>
              <div>
                <Label htmlFor='name'>
                  Full Name<span className='ml-1 text-destructive'>*</span>
                </Label>
                <Input id='name' placeholder='John Doe' {...register("name")} />
                {errors.name && (
                  <p className='text-sm text-destructive'>
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor='email'>
                  Email<span className='ml-1 text-destructive'>*</span>
                </Label>
                <Input
                  type='email'
                  id='email'
                  placeholder='youremail@example.com'
                  {...register("email")}
                />
                {errors.email && (
                  <p className='text-sm text-destructive'>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <PhoneNumberInput
                value={phone}
                onChange={(val) => {
                  setPhone(val);
                  setValue("phone", val);
                  trigger("phone");
                }}
                error={errors.phone?.message}
                required={true}
              />

              <div>
                <Label htmlFor='projectType'>
                  Service Interest
                  <span className='ml-1 text-destructive'>*</span>
                </Label>
                <Select
                  onValueChange={(val) => {
                    setValue("projectType", val);
                    trigger("projectType");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select a service' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='SOCIAL_MEDIA_MANAGEMENT'>
                      Social Media Management
                    </SelectItem>
                    <SelectItem value='CONTENT_CREATION'>
                      Content Creation & Strategy
                    </SelectItem>
                    <SelectItem value='PAID_ADVERTISING'>
                      Paid Advertising Campaigns
                    </SelectItem>
                    <SelectItem value='ANALYTICS_GROWTH'>
                      Analytics & Growth Strategy
                    </SelectItem>
                    <SelectItem value='FULL_PACKAGE'>
                      Full Package (All Services + Website)
                    </SelectItem>
                    <SelectItem value='WEBSITE_ONLY'>
                      Custom Website Only
                    </SelectItem>
                    <SelectItem value='CONSULTATION'>
                      Consultation / Guidance
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.projectType && (
                  <p className='text-sm text-destructive'>
                    {errors.projectType.message}
                  </p>
                )}
              </div>

              <CurrencyBudgetInput
                value={budget}
                onChange={(val) => {
                  setBudget(val);
                  setValue("budget", val);
                  trigger("budget");
                }}
                error={errors.budget?.message}
                required={true}
              />

              <DateInput
                value={deadline}
                onChange={(val) => {
                  setDeadline(val);
                  setValue("deadline", val);
                }}
                label='Project Deadline'
                placeholder="Enter a date or timeframe (e.g., '2 weeks', 'End of May')"
              />

              <div>
                <Label htmlFor='message'>
                  Tell Us About Your Brand & Goals
                  <span className='ml-1 text-destructive'>*</span>
                </Label>
                <Textarea
                  id='message'
                  rows={6}
                  placeholder='Tell us about your business, target audience, and social media goals...'
                  {...register("message")}
                />
                {errors.message && (
                  <p className='text-sm text-destructive'>
                    {errors.message.message}
                  </p>
                )}
              </div>

              <Button
                type='submit'
                className='w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700'
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Sending..."
                  : "🎁 Claim My Free Month + Website"}
              </Button>

              <p className='text-center text-xs text-muted-foreground'>
                Fields marked with <span className='text-destructive'>*</span>{" "}
                are required
              </p>
            </form>
          </>
        ) : (
          <div className='text-center'>
            {isSuccess ? (
              <div className='space-y-4'>
                <h2 className='text-3xl font-semibold text-green-600'>
                  🎉 Welcome to Rolex Studio!
                </h2>
                <p className='text-muted-foreground'>
                  Your free month + website package is reserved! We'll contact
                  you within 24 hours to get started on amplifying your brand.
                </p>
                <Button onClick={() => setIsSubmitted(false)} className='mt-4'>
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <div className='space-y-4'>
                <h2 className='text-3xl font-semibold text-red-600'>
                  Something Went Wrong
                </h2>
                <p className='text-muted-foreground'>
                  There was an issue with your submission. Please try again
                  later.
                </p>
                <Button onClick={() => setIsSubmitted(false)} className='mt-4'>
                  Try Again
                </Button>
              </div>
            )}
          </div>
        )}
      </Container>
    </Section>
  );
};

export default ContactPage;
